import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience, IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { ErroDataType } from '@dadohr/shared/domainTypes/instance';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { Checkbox, Dialog, Tooltip } from '@material-ui/core';
import { ChevronDown, ChevronUp, Info, RefreshCw, Wrench } from 'lucide-react';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import useIsMounted from '../../../../../hooks/useIsMounted';
import { DialogActionsHeader } from '../../../../../components/DialogActionsHeader';
import { DialogActionsWithSlots } from '../../../../../components/DialogActionsWithSlots';
import { DialogContentWithoutPadding } from '../../../../../components/DialogContentWithoutPadding';
import { Loader } from '../../../../../components/Loader';
import { UnsafeTFunction } from '../../../../../i18next';
import { useMappedLoadingValue } from '../../../../../services/firebase';
import { toFullName, useUserMapById } from '../../../../../services/user';
import { NewUxButton, NewUxButtonWithPromise } from '../../../components/Builders/newux/kit/Button';
import { SectionLabel, StatusChip, TextLinkButton } from '../../../components/Builders/newux/kit/controls';
import { tokens } from '../../../components/Builders/newux/kit/tokens';
import type { ExperienceProblemData } from '../InstanceProblems';
import { getIssueTitleText } from '../ParticipantDialog';
import { CategoryIcon } from './categoryIcon';
import { getInstanceFromRaw, getOccurrenceParticipantUserId, getOccurrenceTimestamp, GroupedIssue } from './group';
import { copyFor } from './newCopy';
import { timelineItemRefFor } from './timelineItemRef';
import { Issue, IssueCopyContext } from './types';
import { useGroupedIssues } from './useGroupedIssues';

const { color, font } = tokens;

const ModalTitle = styled('div')`
  font-size: ${font.variant.h4.fontSize}px;
  line-height: ${font.variant.h4.lineHeight}px;
  font-weight: ${font.variant.h4.fontWeight};
  color: ${color.black};
`;

const ContentPadding = styled('div')`
  padding: ${tokens.space(6)};
`;

const ContentMaxWidth = styled('div')`
  max-width: 1280px;
  margin: 0 auto;
`;

const CategorySection = styled('div')`
  margin-bottom: ${tokens.space(8)};
  &:last-child {
    margin-bottom: 0;
  }
`;

const CategorySectionLabel = styled(SectionLabel)`
  display: flex;
  align-items: center;
  gap: ${tokens.space(1.5)};
  margin-top: 0;
`;

const CategoryRowsBlock = styled('div')`
  border: 1px solid ${color.borderSubtle};
  border-radius: ${tokens.radius.sm}px;
  overflow: hidden;
`;

const GroupRowBox = styled('div')`
  background: ${color.white};
  & + & {
    border-top: 1px solid ${color.borderSubtle};
  }
`;

const GroupRowHeader = styled('div')<{ expanded?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
  height: 56px;
  padding: 0 ${tokens.space(3)};
  background: ${(p) => (p.expanded ? color.grey100 : 'transparent')};
  &:hover {
    background: ${color.grey100};
  }
  /* Bulk buttons stay visible while this group is expanded; otherwise they
     show on hover only, or while keyboard focus is on one of them. opacity
     (not display/visibility) keeps their space reserved so the title/count
     don't shift, and keeps them tabbable; pointer-events: none stops the
     invisible buttons taking clicks (the pointer falls through to the row,
     whose hover then reveals them). The focus rule is scoped to the buttons
     themselves — the row's chevron is also a button, and a row-level
     :focus-within would pin the pair open after any expand/collapse click. */
  & .group-actions {
    opacity: ${(p) => (p.expanded ? 1 : 0)};
    pointer-events: ${(p) => (p.expanded ? 'auto' : 'none')};
  }
  &:hover .group-actions,
  & .group-actions:focus-within {
    opacity: 1;
    pointer-events: auto;
  }
  /* A button mid-action carries data-busy, so this keeps the pair visible
     until it finishes even if the pointer has left. Keyed on data-busy rather
     than :disabled because a bulk button is also disabled, while idle, when
     another button is acting on its rows. Kept as its own rule: a browser
     without :has() drops a whole selector list that contains it, and the
     hover/focus reveal above must survive that. */
  & .group-actions:has(button[data-busy]) {
    opacity: 1;
    pointer-events: auto;
  }
`;

const RowTitle = styled('div')`
  flex: 1 1 auto;
  min-width: 0;
  font-size: ${font.variant.h6.fontSize}px;
  line-height: ${font.variant.h6.lineHeight}px;
  font-weight: ${font.variant.h6.fontWeight};
  color: ${color.black};
  cursor: pointer;
`;

const ChevronButton = styled('button')`
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${tokens.space(1)};
  color: ${color.grey700};
`;

const GroupRowDetail = styled('div')`
  padding: ${tokens.space(6)};
  border-top: 1px solid ${color.borderSubtle};
  background: ${color.grey100};
`;

const TwoColumnGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${tokens.space(6)};
`;

const DetailSectionHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(1.5)};
  font-size: ${font.variant.h7.fontSize}px;
  line-height: ${font.variant.h7.lineHeight}px;
  font-weight: ${font.variant.h7.fontWeight};
  text-transform: ${font.variant.h7.textTransform};
  color: ${color.grey500};
  margin-bottom: ${tokens.space(1.5)};
`;

const DetailBodyText = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  line-height: ${font.variant.caption.lineHeight}px;
  font-weight: ${font.variant.caption.fontWeight};
  color: ${color.black};
  & ul {
    margin: 0;
    padding-left: 18px;
  }
  & li ul {
    margin-top: ${tokens.space(1)};
  }
  & li {
    margin-bottom: ${tokens.space(1)};
  }
  & > ul > li:last-child {
    margin-bottom: 0;
  }
`;

const SubTable = styled('table')`
  margin-top: ${tokens.space(6)};
  border: 1px solid ${color.borderSubtle};
  border-radius: ${tokens.radius.sm}px;
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  overflow: hidden;
  background: ${color.white};
`;

const SubTableHeaderRow = styled('tr')`
  background: ${color.grey100};
`;

const SubTableRow = styled('tr')`
  background: ${color.white};
  & + & > td {
    border-top: 1px solid ${color.borderSubtle};
  }
`;

/** Every column except Timeline item sizes to its own content and is never
 * truncated — `width: 1px` plus `white-space: nowrap` is the standard trick
 * that makes an auto-layout table shrink a column down to its content's
 * natural width instead of sharing space evenly. */
const SubTableFixedCell = styled('td')`
  width: 1px;
  white-space: nowrap;
  vertical-align: middle;
  padding: ${tokens.space(1)} ${tokens.space(3)};
`;

const SubTableFixedHeadCell = styled('th')`
  width: 1px;
  white-space: nowrap;
  text-align: left;
  font-weight: normal;
  vertical-align: middle;
  padding: ${tokens.space(1)} ${tokens.space(3)};
`;

/** Timeline item is the one column allowed to absorb the remaining space and
 * truncate — `max-width: 0` alongside the fixed columns' `width: 1px` is
 * what makes auto table layout actually compress it instead of growing the
 * table past its container. */
const SubTableItemCell = styled('td')`
  max-width: 0;
  width: 100%;
  vertical-align: middle;
  padding: ${tokens.space(1)} ${tokens.space(3)};
`;

const SubTableItemHeadCell = styled('th')`
  width: 100%;
  text-align: left;
  font-weight: normal;
  vertical-align: middle;
  padding: ${tokens.space(1)} ${tokens.space(3)};
`;

const ActionButtonRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(1.5)};
`;

const SubTableHeaderLabel = styled('div')`
  font-size: ${font.variant.h7.fontSize}px;
  line-height: ${font.variant.h7.lineHeight}px;
  font-weight: ${font.variant.h7.fontWeight};
  text-transform: ${font.variant.h7.textTransform};
  color: ${color.grey500};
`;

const SubTableCellText = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  line-height: ${font.variant.caption.lineHeight}px;
  font-weight: ${font.variant.caption.fontWeight};
  color: ${color.black};
`;

const SubTableDateText = styled(SubTableCellText)`
  color: ${color.grey700};
`;

const SubTableItemTitle = styled(SubTableCellText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* The link is a button, so it needs its own truncation; the cell's ellipsis
 * only applies to its direct text. */
const ItemTitleLinkButton = styled(TextLinkButton)`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

const groupKeyFor = (g: GroupedIssue) => `${g.kind}::${g.groupKey}`;

/** Same title resolver the legacy table's "Timeline item" column already
 * uses (`IssueTitle` → `getIssueTitleText`, `ParticipantDialog.tsx`) — every
 * non-AUTOMATION family's `raw.data` is the original `ExperienceProblemData`
 * that function expects. AUTOMATION doesn't retain the full original object,
 * just the title string already pulled off it (`automationTitle`). */
const occurrenceItemTitle = (
  issue: Issue,
  t: UnsafeTFunction,
  participant: Doc<IUser> | undefined
): string => {
  const raw = issue.raw;
  if (raw.family === 'AUTOMATION') return raw.automationTitle || '';
  return getIssueTitleText(raw.data as ExperienceProblemData, t, participant) || '';
};

/** `onRetry`/`onIgnore` (and the tracking calls they fire before branching)
 * expect the full `ExperienceProblemData` — every non-AUTOMATION family's
 * `raw.data` already is that, unchanged. AUTOMATION's `Issue.raw` doesn't
 * retain the original object, so this reconstructs the minimal shape those
 * call paths actually read: `toIssueRaw`'s AUTOMATION branch (shared by both
 * copy packs) only reads `denormalized`/`instance`/`automationTpl.id`/`.title`
 * — all present below, sourced from `raw.action`/`raw.instance`/
 * `raw.automationTplId`/`raw.automationTitle`. `legacyCopy/automation.tsx`
 * (every entry, grepped) never dereferences `.automationTpl.`/`.tpl.` beyond
 * that either. `action`/`id` are required by `AutomationActionErrorData`'s
 * type but never read by anything in this path, so they're stubbed from
 * `raw.action` itself rather than left as guesses. */
const toProblemData = (issue: Issue): ExperienceProblemData => {
  const raw = issue.raw;
  if (raw.family !== 'AUTOMATION') return raw.data as ExperienceProblemData;
  // `automationTpl`/`actionTpl`/`action`/`id` are required by the type but
  // never read by any call path this feeds — stubbed rather than faked with
  // full doc shapes that don't exist here.
  return ({
    type: 'AUTOMATION',
    tpl: { id: raw.instance?.data?.templateId },
    instance: raw.instance,
    id: raw.action.id,
    automationTpl: { id: raw.automationTplId, title: raw.automationTitle },
    actionTpl: { id: raw.action.templateId },
    action: raw.action,
    denormalized: raw.action
  } as unknown) as ExperienceProblemData;
};

/** The legacy table's rule (no Retry for a missing actor, or for a sent item
 * with no channels to resend on) plus the two types `onRetryHandler` has no
 * retry path for — see its fall-through comment in `../index.tsx`. */
const showRetryButton = (d: ExperienceProblemData): boolean =>
  d.type !== 'MISSING_ACTOR' &&
  d.type !== 'CC_SEND_ERROR' &&
  d.type !== ErroDataType.CANNOT_COMPLETE_LMS_MODULE &&
  (d.type !== 'SENT_ITEM' || !!d.channels.length);

type UsersById = { [id: string]: Doc<IUser> } | undefined;
type ProblemDataWithParticipant = ExperienceProblemData & { participant?: Doc<IUser> };

type SingleAction = (d: ExperienceProblemData) => Promise<unknown>;
type BulkAction = (ds: ExperienceProblemData[]) => Promise<unknown>;
type RowAction = 'retry' | 'ignore';
/** Rows (by `selectionKey`) with a retry or ignore in flight, from any
 * button that covers them: their own, their group header's, or the footer's. */
type InFlightRows = ReadonlyMap<string, RowAction>;
/** Runs `run` with `keys` marked in flight for its duration. */
type RunMarked = (action: RowAction, keys: string[], run: () => Promise<unknown>) => Promise<unknown>;
/** An occurrence paired with its selection key, for the bulk buttons. */
type RowTarget = { key: string; data: ProblemDataWithParticipant };
/** Milliseconds until an issue may be retried again; > 0 means a retry is
 * already queued (SC-36146). */
type RetryCooldown = (d: ExperienceProblemData) => number;

/** Stable per-occurrence key — the raw error's own id, scoped to its
 * instance — so row selection survives rows above it disappearing after a
 * retry or ignore. */
const occurrenceId = (issue: Issue): string => {
  const raw = issue.raw;
  const instanceId = getInstanceFromRaw(raw)?.id || '';
  const id = raw.family === 'AUTOMATION' ? raw.action.id : (raw.data as { id?: string }).id || '';
  return `${instanceId}:${id}`;
};

/** Selection is one modal-wide set keyed per group, so the footer can act on
 * rows selected across groups and an occurrence id that repeats across
 * groups (a missing-actor row is keyed by instance id, once per role) can't
 * collide. */
const selectionKey = (g: GroupedIssue, issue: Issue): string => `${groupKeyFor(g)}|${occurrenceId(issue)}`;

/** The retry/ignore handlers read `d.participant` for the tracking payload's
 * title (the legacy table attaches it to every row too). */
const problemDataFor = (issue: Issue, usersById: UsersById): ProblemDataWithParticipant => {
  const participantUserId = getOccurrenceParticipantUserId(issue);
  return {
    ...toProblemData(issue),
    participant: participantUserId ? usersById?.[participantUserId] : undefined
  };
};

/** Ignore + Retry pair acting on a set of occurrences; used on each group
 * header and in the modal footer. Retry is omitted when nothing in the set
 * can be retried. */
const BulkActionButtons: React.FC<{
  ignoreTargets: RowTarget[];
  retryTargets: RowTarget[];
  ignoreLabel: string;
  retryLabel: string;
  onRetryMany: BulkAction;
  onIgnoreMany: BulkAction;
  inFlight: InFlightRows;
  runMarked: RunMarked;
  onDone: () => void;
  className?: string;
}> = ({
  ignoreTargets,
  retryTargets,
  ignoreLabel,
  retryLabel,
  onRetryMany,
  onIgnoreMany,
  inFlight,
  runMarked,
  onDone,
  className
}) => {
  // A bulk button waits while any of its rows is already being acted on, and
  // shows the pending state itself when every one of its rows is in flight
  // with its own action (e.g. the footer retried them all).
  const anyInFlight = (targets: RowTarget[]) => targets.some((t) => inFlight.has(t.key));
  const allInFlightWith = (targets: RowTarget[], action: RowAction) =>
    targets.length > 0 && targets.every((t) => inFlight.get(t.key) === action);
  const run = (action: RowAction, targets: RowTarget[], bulk: BulkAction) =>
    runMarked(
      action,
      targets.map((t) => t.key),
      () => bulk(targets.map((t) => t.data))
    ).then(onDone);
  return (
    <ActionButtonRow className={className}>
      <NewUxButtonWithPromise
        variant="outlined"
        color="neutral"
        size="small"
        pending="Ignoring…"
        busy={allInFlightWith(ignoreTargets, 'ignore')}
        disabled={anyInFlight(ignoreTargets)}
        onClick={() => run('ignore', ignoreTargets, onIgnoreMany)}
      >
        {ignoreLabel}
      </NewUxButtonWithPromise>
      {retryTargets.length > 0 && (
        <NewUxButtonWithPromise
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<RefreshCw size={14} strokeWidth={1.5} />}
          pending="Retrying…"
          busy={allInFlightWith(retryTargets, 'retry')}
          disabled={anyInFlight(retryTargets)}
          onClick={() => run('retry', retryTargets, onRetryMany)}
        >
          {retryLabel}
        </NewUxButtonWithPromise>
      )}
    </ActionButtonRow>
  );
};

/** WHAT CAUSED IT / HOW TO FIX IT + the itemized per-occurrence sub-table for
 * one expanded group. Row selection lives on the parent `GroupRow`, since the
 * group header's bulk buttons act on it. */
const GroupRowExpandedContent: React.FC<{
  g: GroupedIssue;
  usersById: UsersById;
  selectedRows: Set<string>;
  onToggleRow: (id: string) => void;
  onRetry: SingleAction;
  onIgnore: SingleAction;
  getRetryCooldownMs: RetryCooldown;
  inFlight: InFlightRows;
  runMarked: RunMarked;
}> = ({ g, usersById, selectedRows, onToggleRow, onRetry, onIgnore, getRetryCooldownMs, inFlight, runMarked }) => {
  const kindCopy = copyFor(g.kind);

  return (
    <>
      <TwoColumnGrid>
        <div>
          <DetailSectionHeader>
            <Info size={14} strokeWidth={1.5} />
            What caused it
          </DetailSectionHeader>
          <DetailBodyText>{kindCopy.whatCausedIt(g.occurrences)}</DetailBodyText>
        </div>
        <div>
          <DetailSectionHeader>
            <Wrench size={14} strokeWidth={1.5} />
            How to fix it
          </DetailSectionHeader>
          <DetailBodyText>{kindCopy.howToFixIt(g.occurrences)}</DetailBodyText>
        </div>
      </TwoColumnGrid>
      <SubTable>
        <thead>
          <SubTableHeaderRow>
            <SubTableFixedHeadCell />
            <SubTableItemHeadCell>
              <SubTableHeaderLabel>Timeline item</SubTableHeaderLabel>
            </SubTableItemHeadCell>
            <SubTableFixedHeadCell>
              <SubTableHeaderLabel>Participant</SubTableHeaderLabel>
            </SubTableFixedHeadCell>
            <SubTableFixedHeadCell>
              <SubTableHeaderLabel>Last attempt</SubTableHeaderLabel>
            </SubTableFixedHeadCell>
            {/* Invisible but layout-occupying: forces this row to the same
             * actual height as a data row (driven by the buttons' real
             * rendered size) instead of guessing a matching pixel value. */}
            <SubTableFixedHeadCell>
              <ActionButtonRow style={{ visibility: 'hidden' }}>
                <NewUxButton variant="outlined" color="neutral" size="small">
                  Ignore
                </NewUxButton>
                <NewUxButton variant="outlined" color="primary" size="small" startIcon={<RefreshCw size={14} strokeWidth={1.5} />}>
                  Retry
                </NewUxButton>
              </ActionButtonRow>
            </SubTableFixedHeadCell>
          </SubTableHeaderRow>
        </thead>
        <tbody>
          {g.occurrences.map((issue) => {
            const id = occurrenceId(issue);
            const key = selectionKey(g, issue);
            const problemData = problemDataFor(issue, usersById);
            const { participant } = problemData;
            const lastAttempt = getOccurrenceTimestamp(issue);
            const rowAction = inFlight.get(key);
            const itemTitle = occurrenceItemTitle(issue, issue.ctx.t as UnsafeTFunction, participant);
            const itemRef = timelineItemRefFor(issue.raw);
            return (
              <SubTableRow key={id}>
                <SubTableFixedCell>
                  <Checkbox size="small" checked={selectedRows.has(key)} onChange={() => onToggleRow(key)} />
                </SubTableFixedCell>
                <SubTableItemCell>
                  <SubTableItemTitle>
                    {itemRef && itemTitle && issue.ctx.onOpenTimelineItem ? (
                      <ItemTitleLinkButton label={itemTitle} onClick={() => issue.ctx.onOpenTimelineItem?.(itemRef)} />
                    ) : (
                      itemTitle
                    )}
                  </SubTableItemTitle>
                </SubTableItemCell>
                <SubTableFixedCell>
                  <SubTableCellText>
                    {participant && issue.ctx.onOpenParticipant && (
                      <TextLinkButton
                        label={toFullName(participant.data)}
                        onClick={() => {
                          const instance = getInstanceFromRaw(issue.raw);
                          if (instance) issue.ctx.onOpenParticipant?.(instance);
                        }}
                      />
                    )}
                    {participant && !issue.ctx.onOpenParticipant && toFullName(participant.data)}
                  </SubTableCellText>
                </SubTableFixedCell>
                <SubTableFixedCell>
                  <SubTableDateText>{lastAttempt ? moment(lastAttempt).format('YYYY/MM/DD HH:mm') : ''}</SubTableDateText>
                </SubTableFixedCell>
                <SubTableFixedCell>
                  <ActionButtonRow>
                    <NewUxButtonWithPromise
                      variant="outlined"
                      color="neutral"
                      size="small"
                      pending="Ignoring…"
                      busy={rowAction === 'ignore'}
                      disabled={!!rowAction}
                      onClick={() => runMarked('ignore', [key], () => onIgnore(problemData))}
                    >
                      Ignore
                    </NewUxButtonWithPromise>
                    {showRetryButton(problemData) && getRetryCooldownMs(problemData) > 0 && (
                      <Tooltip title="Retries can take up to 2 minutes. If unsuccessful, the issue will re-appear here automatically.">
                        <span>
                          <StatusChip tone="blue">Retry in progress</StatusChip>
                        </span>
                      </Tooltip>
                    )}
                    {showRetryButton(problemData) && getRetryCooldownMs(problemData) === 0 && (
                      <NewUxButtonWithPromise
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<RefreshCw size={14} strokeWidth={1.5} />}
                        pending="Retrying…"
                        busy={rowAction === 'retry'}
                        disabled={!!rowAction}
                        onClick={() => runMarked('retry', [key], () => onRetry(problemData))}
                      >
                        Retry
                      </NewUxButtonWithPromise>
                    )}
                  </ActionButtonRow>
                </SubTableFixedCell>
              </SubTableRow>
            );
          })}
        </tbody>
      </SubTable>
    </>
  );
};

/** One accordion row: header plus the expanded
 * detail. Row selection is the modal-wide set, so the header buttons read it
 * whether or not the group is expanded and the footer can act across groups. */
const GroupRow: React.FC<{
  g: GroupedIssue;
  expanded: boolean;
  onToggleExpanded: () => void;
  usersById: UsersById;
  selectedRows: Set<string>;
  onToggleRow: (key: string) => void;
  onSetRows: (keys: string[], selected: boolean) => void;
  onRetry: SingleAction;
  onIgnore: SingleAction;
  onRetryMany: BulkAction;
  onIgnoreMany: BulkAction;
  getRetryCooldownMs: RetryCooldown;
  inFlight: InFlightRows;
  runMarked: RunMarked;
}> = ({
  g,
  expanded,
  onToggleExpanded,
  usersById,
  selectedRows,
  onToggleRow,
  onSetRows,
  onRetry,
  onIgnore,
  onRetryMany,
  onIgnoreMany,
  getRetryCooldownMs,
  inFlight,
  runMarked
}) => {
  const kindCopy = copyFor(g.kind);
  // One key + problem-data pair per occurrence, built once per group rather
  // than on every selection or in-flight change.
  const rowTargets = useMemo<RowTarget[]>(
    () => g.occurrences.map((o) => ({ key: selectionKey(g, o), data: problemDataFor(o, usersById) })),
    [g, usersById]
  );
  const groupKeys = rowTargets.map((t) => t.key);
  const selectedTargets = rowTargets.filter((t) => selectedRows.has(t.key));
  const n = selectedTargets.length;
  const allSelected = n > 0 && n === rowTargets.length;

  const ignoreTargets = n ? selectedTargets : rowTargets;
  const retryTargets = ignoreTargets.filter((t) => showRetryButton(t.data) && getRetryCooldownMs(t.data) === 0);

  return (
    <GroupRowBox>
      <GroupRowHeader expanded={expanded}>
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={n > 0 && !allSelected}
          onChange={() => onSetRows(groupKeys, !allSelected)}
        />
        <RowTitle onClick={onToggleExpanded}>{kindCopy.summary(g.occurrences)}</RowTitle>
        <BulkActionButtons
          className="group-actions"
          ignoreTargets={ignoreTargets}
          retryTargets={retryTargets}
          ignoreLabel={n ? `Ignore ${n}` : 'Ignore all'}
          retryLabel={n ? `Retry ${retryTargets.length}` : 'Retry all'}
          onRetryMany={onRetryMany}
          onIgnoreMany={onIgnoreMany}
          inFlight={inFlight}
          runMarked={runMarked}
          onDone={() => onSetRows(groupKeys, false)}
        />
        <StatusChip tone="grey">{plural(g.count, 'issue')}</StatusChip>
        <ChevronButton type="button" aria-label={expanded ? 'Collapse' : 'Expand'} onClick={onToggleExpanded}>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </ChevronButton>
      </GroupRowHeader>
      {expanded && (
        <GroupRowDetail>
          <GroupRowExpandedContent
            g={g}
            usersById={usersById}
            selectedRows={selectedRows}
            onToggleRow={onToggleRow}
            onRetry={onRetry}
            onIgnore={onIgnore}
            getRetryCooldownMs={getRetryCooldownMs}
            inFlight={inFlight}
            runMarked={runMarked}
          />
        </GroupRowDetail>
      )}
    </GroupRowBox>
  );
};

export const IssuesModal: React.FC<{
  open: boolean;
  onClose: () => void;
  tpl: Doc<IExperienceTemplate>;
  instances: Doc<IExperience>[];
  org: IOrganization;
  ctx: IssueCopyContext;
  /** Same `onRetryHandler`/`onIgnoreHandler` the legacy table already calls —
   * see `toProblemData` above for how each occurrence is turned into the
   * `ExperienceProblemData` these expect. */
  onRetry: SingleAction;
  onIgnore: SingleAction;
  onRetryMany: BulkAction;
  onIgnoreMany: BulkAction;
  getRetryCooldownMs: RetryCooldown;
  /** Shown above the groups, e.g. the "N retries in progress" notice. */
  banner?: React.ReactNode;
}> = ({ open, onClose, tpl, instances, org, ctx, onRetry, onIgnore, onRetryMany, onIgnoreMany, getRetryCooldownMs, banner }) => {
  const { groups, loading } = useGroupedIssues(tpl, instances, org, ctx);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // Modal-wide row selection (see `selectionKey`). Keys of rows that have
  // since disappeared are simply never matched rather than pruned.
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const toggleRow = (key: string) =>
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const setRows = (keys: string[], selected: boolean) =>
    setSelectedRows((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (selected ? next.add(k) : next.delete(k)));
      return next;
    });

  // Rows with a retry or ignore in flight, whichever button started it, so
  // every other button covering those rows waits, and shows the same pending
  // state when all of its rows are covered. An action that settles after the
  // modal has closed must not touch state, hence the mounted guard.
  const isMounted = useIsMounted();
  const [inFlight, setInFlight] = useState<Map<string, RowAction>>(new Map());
  const runMarked: RunMarked = (action, keys, run) => {
    setInFlight((prev) => {
      const next = new Map(prev);
      keys.forEach((k) => next.set(k, action));
      return next;
    });
    const clear = () => {
      if (!isMounted.current) return;
      setInFlight((prev) => {
        const next = new Map(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    };
    // `.then(run)` turns a synchronous throw into a rejection, so `finally`
    // still clears the rows.
    return Promise.resolve().then(run).finally(clear);
  };

  const byCategory = useMemo(() => {
    const map = new Map<string, GroupedIssue[]>();
    groups.forEach((g) => {
      const category = copyFor(g.kind).category;
      const list = map.get(category) || [];
      list.push(g);
      map.set(category, list);
    });
    // Within a category, oldest last attempt first; groups with no
    // timestamp at all go to the end.
    map.forEach((list) =>
      list.sort((a, b) => (a.lastAttemptAt ?? Infinity) - (b.lastAttemptAt ?? Infinity))
    );
    return map;
  }, [groups]);

  const totalCount = groups.reduce((sum, g) => sum + g.count, 0);

  // One lookup for every occurrence's participant. `useUserMapById` is a
  // client-side pick over the org's shared users listener, so resolving all
  // groups at once costs no extra reads and lets the header/footer bulk
  // buttons build the same tracking payload as a single click.
  const participantUserIds = useMemo(() => {
    const ids = new Set<string>();
    groups.forEach((g) =>
      g.occurrences.forEach((issue) => {
        const id = getOccurrenceParticipantUserId(issue);
        if (id) ids.add(id);
      })
    );
    return Array.from(ids);
  }, [groups]);
  const [usersByIdValue] = useMappedLoadingValue(useUserMapById(org.id, participantUserIds), (m) => m, true);
  const usersById: UsersById = usersByIdValue || undefined;

  // The footer acts on every selected row across all groups.
  const footerIgnoreTargets: RowTarget[] = [];
  groups.forEach((g) =>
    g.occurrences.forEach((o) => {
      const key = selectionKey(g, o);
      if (selectedRows.has(key)) footerIgnoreTargets.push({ key, data: problemDataFor(o, usersById) });
    })
  );
  const footerRetryTargets = footerIgnoreTargets.filter(
    (t) => showRetryButton(t.data) && getRetryCooldownMs(t.data) === 0
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogActionsHeader
        onClose={onClose}
        left={<ModalTitle>{plural(totalCount, 'issue')} {totalCount === 1 ? 'needs' : 'need'} attention</ModalTitle>}
      />
      <DialogContentWithoutPadding>
        <ContentPadding>
        <ContentMaxWidth>
        {banner}
        {loading && <Loader />}
        {!loading &&
          [...byCategory.entries()].map(([category, categoryGroups]) => (
            <CategorySection key={category}>
              <CategorySectionLabel>
                <CategoryIcon category={category} size={14} />
                {category}
              </CategorySectionLabel>
              <CategoryRowsBlock>
                {categoryGroups.map((g) => {
                  const key = groupKeyFor(g);
                  const expanded = expandedKey === key;
                  return (
                    <GroupRow
                      key={key}
                      g={g}
                      expanded={expanded}
                      onToggleExpanded={() => setExpandedKey(expanded ? null : key)}
                      usersById={usersById}
                      selectedRows={selectedRows}
                      onToggleRow={toggleRow}
                      onSetRows={setRows}
                      onRetry={onRetry}
                      onIgnore={onIgnore}
                      onRetryMany={onRetryMany}
                      onIgnoreMany={onIgnoreMany}
                      getRetryCooldownMs={getRetryCooldownMs}
                      inFlight={inFlight}
                      runMarked={runMarked}
                    />
                  );
                })}
              </CategoryRowsBlock>
            </CategorySection>
          ))}
        </ContentMaxWidth>
        </ContentPadding>
      </DialogContentWithoutPadding>
      <DialogActionsWithSlots
        right={
          <ActionButtonRow>
            {footerIgnoreTargets.length > 0 && (
              <BulkActionButtons
                ignoreTargets={footerIgnoreTargets}
                retryTargets={footerRetryTargets}
                ignoreLabel={`Ignore ${footerIgnoreTargets.length}`}
                retryLabel={`Retry ${footerRetryTargets.length}`}
                onRetryMany={onRetryMany}
                onIgnoreMany={onIgnoreMany}
                inFlight={inFlight}
                runMarked={runMarked}
                onDone={() => setSelectedRows(new Set())}
              />
            )}
            <NewUxButton variant="outlined" color="neutral" size="small" onClick={onClose}>
              Close
            </NewUxButton>
          </ActionButtonRow>
        }
      />
    </Dialog>
  );
};
